/**
 * ProviderProfileModal - View-only profile modal for Client Portal
 * Uses token-based auth instead of JWT
 */
import React, { useState, useEffect } from 'react';
import { Briefcase, GraduationCap, Loader2, Languages as LanguagesIcon, Users, Video } from 'lucide-react';

const AGES_SERVED_LABELS = { adults: 'Adults', children: 'Children', both: 'Adults & Children' };
const SERVICE_DELIVERY_LABELS = { telehealth: 'Telehealth', in_person: 'In-Person', both: 'Telehealth & In-Person' };
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ProviderAvatar = ({ provider }) => {
  const [imageError, setImageError] = useState(false);
  
  // Reset error when URL changes (e.g. after GCS refresh)
  useEffect(() => {
    if (provider?.profile_pic_url) {
      setImageError(false);
    }
  }, [provider?.profile_pic_url]);
  
  const getInitials = () => {
    const name = provider?.full_name || '';
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  if (provider?.profile_pic_url && !imageError) {
    return (
      <img 
        // GCS V4 signed URL — DO NOT append cache-busters, see
        // ProfileAvatar.jsx for why.
        src={provider.profile_pic_url}
        alt={provider.full_name}
        className="h-20 w-20 rounded-full object-cover"
        referrerPolicy="no-referrer"
        onError={() => setImageError(true)}
      />
    );
  }

  return (
    <div className="h-20 w-20 rounded-full bg-gradient-to-br from-teal-500 to-teal-700 flex items-center justify-center text-white font-semibold text-xl">
      {getInitials()}
    </div>
  );
};

export const ProviderProfileModal = ({ 
  isOpen, 
  onClose, 
  providerId,
  sessionToken
}) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!isOpen || !providerId || !sessionToken) return;
      
      setLoading(true);
      try {
        const response = await fetch(
          `${API}/profiles/provider/${providerId}/public?token=${sessionToken}`
        );

        if (response.ok) {
          const data = await response.json();
          setProfile(data);
        } else {
          console.error('Provider profile fetch failed:', response.status);
          toast.error('Failed to load provider profile');
        }
      } catch (error) {
        console.error('Error fetching provider profile:', error);
        toast.error('Failed to load provider profile');
      } finally {
        setLoading(false);
      }
    };
    
    fetchProfile();
  }, [isOpen, providerId, sessionToken]);

  const getRoleBadgeColor = (role) => {
    const colors = {
      'super admin': 'bg-purple-100 text-purple-800',
      'admin': 'bg-blue-100 text-blue-800',
      'provider': 'bg-teal-100 text-teal-800',
      'supervisor': 'bg-indigo-100 text-indigo-800',
      'intern': 'bg-amber-100 text-amber-800',
      'office': 'bg-gray-100 text-gray-800',
      'biller': 'bg-green-100 text-green-800',
      'reception': 'bg-pink-100 text-pink-800'
    };
    return colors[role?.toLowerCase()] || 'bg-gray-100 text-gray-800';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="provider-profile-modal">
        <DialogHeader>
          <DialogTitle>Provider Profile</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
          </div>
        ) : profile ? (
          <div className="space-y-6">
            {/* Avatar and Basic Info */}
            <div className="flex items-start gap-4">
              <ProviderAvatar provider={profile} />
              <div className="flex-1 min-w-0">
                <h2 className="text-xl font-semibold text-gray-900 truncate">
                  {profile.full_name || 'Provider'}
                </h2>
                {profile.role && (
                  <Badge className={`mt-1 ${getRoleBadgeColor(profile.role)}`}>
                    {profile.role}
                  </Badge>
                )}
                {profile.credentials && (
                  <p className="text-sm text-gray-600 mt-1">{profile.credentials}</p>
                )}
              </div>
            </div>

            {/* Title */}
            {profile.title && (
              <div className="space-y-2 border-t pt-4">
                <h3 className="text-sm font-medium text-gray-500">Title</h3>
                <div className="flex items-center text-sm text-gray-700">
                  <Briefcase className="h-4 w-4 mr-2 text-teal-600" />
                  {profile.title}
                </div>
              </div>
            )}

            {/* Specialty */}
            {profile.specialty && (
              <div className="space-y-2 border-t pt-4">
                <h3 className="text-sm font-medium text-gray-500">Specialty</h3>
                <div className="flex items-center text-sm text-gray-700">
                  <GraduationCap className="h-4 w-4 mr-2 text-teal-600" />
                  {profile.specialty}
                </div>
              </div>
            )}

            {/* Languages */}
            {profile.languages?.length > 0 && (
              <div className="space-y-2 border-t pt-4" data-testid="provider-languages-section">
                <h3 className="text-sm font-medium text-gray-500 flex items-center gap-1.5">
                  <LanguagesIcon className="h-3.5 w-3.5" /> Languages
                </h3>
                <div className="flex flex-wrap gap-1">
                  {profile.languages.map((lang, idx) => (
                    <Badge key={idx} variant="outline" className="text-xs">{lang}</Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Ages Served / Service Delivery */}
            {(profile.ages_served || profile.service_delivery) && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border-t pt-4">
                {profile.ages_served && (
                  <div className="space-y-1" data-testid="provider-ages-section">
                    <h3 className="text-sm font-medium text-gray-500 flex items-center gap-1.5">
                      <Users className="h-3.5 w-3.5" /> Ages Served
                    </h3>
                    <p className="text-sm text-gray-700">{AGES_SERVED_LABELS[profile.ages_served] || profile.ages_served}</p>
                  </div>
                )}
                {profile.service_delivery && (
                  <div className="space-y-1" data-testid="provider-delivery-section">
                    <h3 className="text-sm font-medium text-gray-500 flex items-center gap-1.5">
                      <Video className="h-3.5 w-3.5" /> Service Mode
                    </h3>
                    <p className="text-sm text-gray-700">{SERVICE_DELIVERY_LABELS[profile.service_delivery] || profile.service_delivery}</p>
                  </div>
                )}
              </div>
            )}

            {/* Bio */}
            {profile.bio && (
              <div className="space-y-2 border-t pt-4">
                <h3 className="text-sm font-medium text-gray-500">About</h3>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">
                  {profile.bio}
                </p>
              </div>
            )}

            {/* Empty state for no additional info */}
            {!profile.title && !profile.specialty && !profile.bio && (
              <div className="text-center py-4 text-gray-500 text-sm border-t">
                No additional profile information available.
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            Profile not found
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ProviderProfileModal;
